import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Section, Eyebrow, H2, Lead, GlassCard } from "../components/site/Primitives";
import StatusBadge from "../components/site/StatusBadge";
import {
  Calendar as Cal, ArrowRight, Crown, Music, Sparkles,
  Heart, Briefcase, Mic, Users, Star, Award,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { log } from "../lib/logger";

const HERO = "https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?w=2200&q=80";

const VENUE_USES = [
  { icon: <Heart size={28} />, name: "Weddings & Receptions", level: "Signature Experience", desc: "Timeless ceremonies and luxury receptions in a black-and-gold setting designed for the most important day.", glow: "#E6B800" },
  { icon: <Briefcase size={28} />, name: "Corporate & Conferences", level: "Business Hospitality", desc: "Board meetings, leadership retreats, product launches, and full-day conferences with AV-ready infrastructure.", glow: "#FFEB99" },
  { icon: <Award size={28} />, name: "Galas & Fundraisers", level: "Mission-Forward", desc: "Nonprofit galas, donor dinners, and community fundraisers — built to elevate the cause and the guest experience.", glow: "#FFCD1A" },
];

const EVENT_TYPES = [
  { icon: <Heart />,    label: "Weddings" },
  { icon: <Briefcase />,label: "Corporate Events" },
  { icon: <Users />,    label: "Conferences" },
  { icon: <Crown />,    label: "Banquets" },
  { icon: <Sparkles />, label: "Fundraisers" },
  { icon: <Mic />,      label: "Concerts" },
  { icon: <Star />,     label: "Networking Nights" },
  { icon: <Award />,    label: "Veteran Events" },
];

export default function Club220() {
  const [events, setEvents] = useState([]);
  useEffect(() => { api.get("/club/events").then((r) => setEvents(r.data.items)).catch((e) => log.warn("club events fetch failed", e?.message)); }, []);

  return (
    <div className="bg-black" data-testid="event-center-page">
      {/* HERO with cinematic gold lighting (layout preserved) */}
      <section className="relative min-h-[95vh] flex items-end overflow-hidden grain">
        <div className="absolute inset-0">
          <motion.img
            src={HERO} alt=""
            className="w-full h-full object-cover"
            initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 14, ease: "linear" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/65 to-black" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-transparent" />
        </div>

        {/* gold spotlight glow */}
        <motion.div
          className="absolute top-1/4 left-1/3 w-[700px] h-[700px] bg-[#E6B800]/20 rounded-full blur-[200px] pointer-events-none"
          animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.7, 0.5] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="absolute bottom-0 right-0 w-[500px] h-[400px] bg-[#E6B800]/10 rounded-full blur-[160px] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-24 pt-40 w-full">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.35em] text-[#E6B800] border border-[#E6B800]/40 px-3 py-2 bg-black/50 mb-8">
              <Crown size={14} /> A 220 Inc. Destination Venue
            </div>
            <div className="mb-6"><StatusBadge kind="in-development" label="Planned Development · Coming Soon" /></div>
            <h1 className="font-display text-6xl sm:text-8xl tracking-tighter font-medium leading-[0.9]">
              <span className="text-white">220</span>{" "}
              <span className="bg-gradient-to-br from-[#FFEB99] via-[#E6B800] to-[#A88300] bg-clip-text text-transparent">Event Center</span>
            </h1>
            <p className="mt-8 text-lg sm:text-2xl text-white/80 max-w-2xl leading-relaxed font-light">
              A planned <span className="text-[#E6B800]">community venue</span> for weddings, corporate events,
              conferences, banquets, fundraisers, and concerts — designed as a destination asset for the region.
            </p>
            <div className="mt-4 text-sm text-white/55 max-w-2xl">
              The 220 Event Center is in development. Bookings are not yet being accepted; please use the form below to register interest and we&apos;ll follow up as we approach opening.
            </div>
            <div className="mt-12 flex flex-wrap gap-4">
              <a href="#reserve" className="inline-flex items-center gap-2 bg-gradient-to-r from-[#FFEB99] via-[#E6B800] to-[#A88300] text-black font-semibold px-8 py-4 hover:from-[#FFCD1A] hover:to-[#E6B800] transition-all" data-testid="event-reserve-cta">
                Register Interest <ArrowRight size={16} />
              </a>
              <a href="#events" className="border border-white/30 text-white px-8 py-4 inline-flex items-center gap-2 hover:border-[#E6B800] hover:text-[#E6B800] transition-colors">
                Planned Programming <Cal size={16} />
              </a>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-8 right-8 hidden md:flex flex-col items-center gap-3 text-[10px] uppercase tracking-[0.4em] text-[#E6B800]/60">
          <span>Columbus, GA · Phenix City, AL</span>
          <div className="w-px h-12 bg-gradient-to-b from-[#E6B800] to-transparent" />
        </div>
      </section>

      {/* Venue Uses (replaces "Lounges" — same 3-column glow card grid) */}
      <Section className="border-t border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-[#E6B800]/8 rounded-full blur-[140px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#FFCD1A]/8 rounded-full blur-[140px]" />
        </div>
        <div className="relative grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {VENUE_USES.map((l) => (
            <div key={l.name} className="bg-black p-10 relative group overflow-hidden">
              <div
                className="absolute -inset-px opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: `radial-gradient(circle at 50% 0%, ${l.glow}22, transparent 70%)` }}
              />
              <div className="relative">
                <div style={{ color: l.glow }} className="mb-5">{l.icon}</div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">{l.level}</div>
                <div className="font-display text-3xl mb-4">{l.name}</div>
                <p className="text-white/65 leading-relaxed">{l.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Event Types strip (replaces "Experience strip") */}
      <Section className="border-t border-white/5">
        <Eyebrow>What We Host</Eyebrow>
        <H2 className="max-w-3xl text-white">A venue for the entire community.</H2>
        <Lead className="mt-4">From private celebrations to public fundraisers — businesses, nonprofits, government partners, and families all find a home here.</Lead>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {EVENT_TYPES.map((f) => (
            <div key={f.label} className="bg-black p-6 sm:p-8 text-center">
              <div className="text-[#E6B800] mb-3 flex justify-center">{f.icon}</div>
              <div className="text-sm">{f.label}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section id="events" className="border-t border-white/5">
        <div className="flex items-center gap-4 flex-wrap mb-2">
          <Eyebrow>Planned Programming</Eyebrow>
          <StatusBadge kind="concept" label="Sample Calendar · Concept" />
        </div>
        <H2 className="max-w-3xl text-white">A look at the planned calendar.</H2>
        <p className="mt-3 text-sm text-white/55 max-w-2xl">Sample programming illustrating the kinds of events the 220 Event Center is being designed to host. Dates and lineups are illustrative until the venue opens.</p>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {events.map((e, i) => (
            <motion.div key={e.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
              <GlassCard className="overflow-hidden group">
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img src={e.image} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                  <div className="absolute bottom-3 left-4 text-xs uppercase tracking-[0.25em] text-[#E6B800]">{e.lounge}</div>
                </div>
                <div className="p-5">
                  <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">{e.date}</div>
                  <div className="font-display text-lg mb-1">{e.title}</div>
                  <div className="text-xs text-[#E6B800]">From ${e.tickets_from || "Free"}</div>
                </div>
              </GlassCard>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section id="reserve" className="border-t border-white/5">
        <Reservation />
      </Section>
    </div>
  );
}

function Reservation() {
  const [f, setF] = useState({
    name: "", email: "", phone: "", date: "", time: "18:00", party_size: 50, table_type: "vip", occasion: "", notes: "",
  });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/club/reservations", { ...f, party_size: Number(f.party_size) }); toast.success("Interest registered. Our events team will follow up as opening dates approach."); }
    catch { toast.error("Could not submit interest."); }
  };
  return (
    <GlassCard className="p-8 md:p-12 relative overflow-hidden" data-testid="event-reservation-form">
      <div className="absolute -top-32 -right-32 w-[400px] h-[400px] bg-[#E6B800]/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <Eyebrow>Register Interest</Eyebrow>
          <H2 className="text-white">Tell us about the event you&apos;d like to host.</H2>
          <Lead className="mt-4">The 220 Event Center is in development. Submit your interest and our events team will follow up as opening dates and packages are confirmed.</Lead>
          <div className="mt-4"><StatusBadge kind="coming-soon" /></div>
          <div className="mt-6 text-sm text-white/55">
            Event Center Contact · Travis R. McGruder · Founder &amp; CEO · <span className="text-white/80">Travis.McGruder@220inc.com</span>
          </div>
        </div>
        <form onSubmit={submit} className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <In label="Name *" required value={f.name} onChange={(e) => set("name", e.target.value)} />
          <In label="Email *" type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} />
          <In label="Phone *" required value={f.phone} onChange={(e) => set("phone", e.target.value)} />
          <In label="Expected Guests *" type="number" min="1" max="40" required value={f.party_size} onChange={(e) => set("party_size", e.target.value)} />
          <In label="Event Date *" type="date" required value={f.date} onChange={(e) => set("date", e.target.value)} />
          <In label="Start Time *" type="time" required value={f.time} onChange={(e) => set("time", e.target.value)} />
          <Se label="Event Type" value={f.table_type} onChange={(e) => set("table_type", e.target.value)} options={[
            { v: "vip", l: "Wedding / Reception" }, { v: "lounge", l: "Corporate / Conference" }, { v: "bottle_service", l: "Banquet / Gala" }, { v: "fire_lounge", l: "Fundraiser" }, { v: "ice_lounge", l: "Concert / Performance" },
          ]} />
          <In label="Occasion" value={f.occasion} onChange={(e) => set("occasion", e.target.value)} />
          <label className="block sm:col-span-2">
            <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Notes</span>
            <textarea rows={3} className="w-full bg-[#0e0e10] border border-white/10 px-3 py-3 text-white text-sm focus:outline-none focus:border-[#E6B800]" value={f.notes} onChange={(e) => set("notes", e.target.value)} />
          </label>
          <div className="sm:col-span-2">
            <button type="submit" className="inline-flex items-center gap-2 bg-gradient-to-r from-[#FFEB99] via-[#E6B800] to-[#A88300] text-black font-semibold px-8 py-4 hover:from-[#FFCD1A] hover:to-[#E6B800] transition-all" data-testid="event-submit-reservation">
              Submit Interest <ArrowRight size={14} />
            </button>
          </div>
        </form>
      </div>
    </GlassCard>
  );
}

function In({ label, ...props }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">{label}</span>
      <input className="w-full bg-[#0e0e10] border border-white/10 px-3 py-3 text-white text-sm focus:outline-none focus:border-[#E6B800]" {...props} />
    </label>
  );
}
function Se({ label, options, ...props }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">{label}</span>
      <select className="w-full bg-[#0e0e10] border border-white/10 px-3 py-3 text-white text-sm focus:outline-none focus:border-[#E6B800]" {...props}>
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}
